<template>
  <div class="app">
    <header class="app-header">
      <div class="brand-row" v-if="showTitle">
        <div class="brand">
          <div class="brand-mark">📂</div>
          <div>
            <div class="brand-title">{{ t('app.title') }}</div>
            <div class="brand-subtitle">{{ t('app.subtitle') }}</div>
          </div>
        </div>
      </div>
      <nav class="steps">
        <router-link
          v-for="(step, i) in steps"
          :key="step.name"
          :to="step.to"
          class="step"
          :class="{ disabled: step.disabled, done: isDone(i, step) }"
        >
          <span v-if="isDone(i, step)" class="step-check" aria-hidden="true">✓</span>
          {{ t(step.label) }}
        </router-link>
      </nav>
    </header>
    <main class="app-main" :class="{ 'with-cfgbar': !embedded }" :style="mainStyle">
      <router-view v-slot="{ Component }">
        <transition :name="transitionName" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
    <!-- Barra de configuració fixa a baix — NOMÉS standalone (embebut: el token
         i la baseUrl els dóna el wrapper de SherpaCRM, no cal barra). -->
    <SettingsPanel v-if="!embedded" />
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SettingsPanel from '@vft/components/SettingsPanel.vue';
import { useAuditStore } from '@vft/stores/auditStore';
import { useDefinitionStore } from '@vft/stores/definitionStore';
import { useMappingStore } from '@vft/stores/mappingStore';
import { useCorrectionsStore } from '@vft/stores/correctionsStore';
import { useComputedStore } from '@vft/stores/computedStore';

const props = defineProps({
  showTitle: { type: Boolean, default: true },
  // Embebut a SherpaCRM → no es pinta la barra de configuració (la connexió ve
  // del wrapper). Standalone (default) → es pinta.
  embedded: { type: Boolean, default: false },
});

const { t } = useI18n();
const route = useRoute();
const auditStore = useAuditStore();
const defStore = useDefinitionStore();
const mappingStore = useMappingStore();
const correctionsStore = useCorrectionsStore();
const computedStore = useComputedStore();

// Mentre algun pas està calculant, marquem el <body> perquè el cursor mostri la
// icona d'ocupat (cursor: progress) a tota l'app.
const busy = computed(() => correctionsStore.running || computedStore.running);
watch(busy, (v) => {
  if (typeof document !== 'undefined') {
    document.body.classList.toggle('vft-busy', v);
  }
});
// Reserva dinàmica d'espai al fons per a la barra CONFIG (fixa i sempre desplegada en
// standalone) perquè no tapi el final del contingut — igual que vue-dashboard mesura la
// seva DevToolbar. En embed no hi ha barra → padding 0.
const cfgbarH = ref(0);
let cfgbarRO = null;
const mainStyle = computed(() => (
  !props.embedded && cfgbarH.value ? { paddingBottom: cfgbarH.value + 'px' } : {}
));
onMounted(() => {
  if (props.embedded || typeof document === 'undefined') return;
  const el = document.querySelector('.cfg-toolbar');
  if (!el) return;
  const measure = () => { cfgbarH.value = el.offsetHeight || 0; };
  requestAnimationFrame(measure);
  if (typeof ResizeObserver !== 'undefined') {
    cfgbarRO = new ResizeObserver(measure);
    cfgbarRO.observe(el);
  }
});
onBeforeUnmount(() => {
  if (typeof document !== 'undefined') {
    document.body.classList.remove('vft-busy');
  }
  if (cfgbarRO) { cfgbarRO.disconnect(); cfgbarRO = null; }
});

// Després del mapeig no es pot avançar si hi ha camps obligatoris sense origen
// ni valor fix (la càrrega a Sherpa fallaria) → es bloquegen els passos posteriors.
const afterMappingBlocked = computed(
  () => mappingStore.mappedFieldCount === 0 || !mappingStore.requiredComplete,
);

const steps = computed(() => [
  { name: 'upload', label: 'nav.upload', to: '/upload', disabled: false },
  { name: 'audit', label: 'nav.audit', to: '/audit', disabled: !auditStore.hasResult },
  { name: 'form', label: 'nav.form', to: '/form', disabled: !auditStore.hasResult },
  { name: 'mapping', label: 'nav.mapping', to: '/mapping', disabled: !defStore.hasDefinition },
  { name: 'values', label: 'nav.values', to: '/values', disabled: afterMappingBlocked.value },
  { name: 'correction', label: 'nav.correction', to: '/correction', disabled: afterMappingBlocked.value },
  { name: 'computed', label: 'nav.computed', to: '/computed', disabled: afterMappingBlocked.value },
  { name: 'export', label: 'nav.export', to: '/export', disabled: afterMappingBlocked.value },
]);

// Índex del pas actual (per la ruta) → els passos anteriors accessibles es marquen
// com a completats (check ✓). Heurística visual: cosmètica, no bloqueja res.
const activeIndex = computed(() => steps.value.findIndex((s) => s.to === route.path));
function isDone(i, step) {
  return !step.disabled && activeIndex.value > -1 && i < activeIndex.value;
}

// Transició entre passos: si s'avança (índex més alt) la vista nova entra per la
// dreta; si es retrocedeix, per l'esquerra. Dóna sensació de pas endavant/enrere.
const STEP_PATHS = ['/upload', '/audit', '/form', '/mapping', '/values', '/correction', '/computed', '/export'];
const transitionName = ref('step-next');
watch(() => route.path, (to, from) => {
  const ti = STEP_PATHS.indexOf(to);
  const fi = STEP_PATHS.indexOf(from);
  transitionName.value = (ti > -1 && fi > -1 && ti < fi) ? 'step-prev' : 'step-next';
});
</script>

<style scoped>
.app {
  min-height: 100%;
  display: flex;
  flex-direction: column;
}
.app-header {
  background: #ffffff;
  border-bottom: 1px solid var(--sherpa-border);
  padding: 0 0 0 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 0;
}
.brand-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.brand {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
.brand-mark {
  font-size: 1.4rem;
}
.brand-title {
  font-family: 'Source Sans Pro', sans-serif;
  font-weight: 400;
  font-size: 1.25rem;
  color: var(--sherpa-ink);
}
.brand-subtitle {
  font-size: 0.78rem;
  color: var(--sherpa-muted);
}
.steps {
  display: flex;
  gap: 1.25rem;
  flex-wrap: wrap;
  border-bottom: 1px solid var(--sherpa-border);
  padding-top: 0.25rem;
}
.step {
  padding: 0.3rem 0.1rem 0.5rem 0.1rem;
  color: var(--sherpa-muted);
  font-size: 0.82rem;
  text-decoration: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color 0.12s, border-color 0.12s;
}
.step:hover {
  color: var(--sherpa-ink);
}
.step.router-link-active {
  color: var(--sherpa-ink);
  border-bottom-color: var(--sherpa-accent);
}
.step.disabled {
  opacity: 0.4;
  pointer-events: none;
}
.step.done { color: var(--sherpa-ink); }
.step-check { color: var(--sherpa-ok); margin-right: 0.25rem; font-size: 0.72rem; }
.app-main {
  flex: 1;
  padding: 1rem 1.5rem 1.5rem;
  background: var(--sherpa-bg);
}
/* Espai inferior per a la barra de configuració fixa (només standalone). */
.app-main.with-cfgbar { padding-bottom: 3rem; }

/* Transició entre passos: fade + lliscament direccional curt (out-in, una vista
   a la vegada per no animar dos DOM pesats alhora). Opacity/transform = compositor. */
.step-next-enter-active, .step-next-leave-active,
.step-prev-enter-active, .step-prev-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
  will-change: opacity, transform;
}
.step-next-enter-from { opacity: 0; transform: translateX(20px); }
.step-next-leave-to   { opacity: 0; transform: translateX(-20px); }
.step-prev-enter-from { opacity: 0; transform: translateX(-20px); }
.step-prev-leave-to   { opacity: 0; transform: translateX(20px); }
@media (prefers-reduced-motion: reduce) {
  .step-next-enter-active, .step-next-leave-active,
  .step-prev-enter-active, .step-prev-leave-active { transition: opacity 0.12s ease; }
  .step-next-enter-from, .step-prev-enter-from,
  .step-next-leave-to, .step-prev-leave-to { transform: none; }
}
</style>
